import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  RoomCategory,
  RoomLayoutType,
  RoomStyle,
} from "@postnatal-care/shared-types";
import { del, get, patch, post, postForm } from "../services/apiClient";

export { RoomCategory, RoomLayoutType, RoomStyle };

export interface RoomPhoto {
  id: string;
  url: string;
  isCover?: boolean;
  sortOrder: number;
  uploadedAt: string;
}

export interface RoomTypeEntity {
  id: string;
  businessId: string;
  name: string;
  category: RoomCategory;
  layoutType?: RoomLayoutType | null;
  occupancy?: string | null;
  style: RoomStyle;
  bedCount: number;
  bedroomCount?: number;
  bathroomCount?: number;
  hasLivingRoom?: boolean;
  hasKitchen?: boolean;
  hasSharedKitchen?: boolean;
  hasCommonBathroom?: boolean;
  hasDiningArea?: boolean;
  hasBalcony?: boolean;
  sqftArea?: number | null;
  pricePerNight: number;
  price30Days?: number | null;
  price40Days?: number | null;
  durationPrices?: { durationNights: number; price: number }[] | null;
  description?: string | null;
  floor?: string | null;
  hasAc: boolean;
  hasMassageRoom: boolean;
  totalRooms: number;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  photos?: RoomPhoto[];
}

export interface CreateRoomPayload {
  businessId: string;
  name: string;
  category?: RoomCategory;
  layoutType?: RoomLayoutType | null;
  occupancy?: string | null;
  style?: RoomStyle;
  bedCount?: number;
  bedroomCount?: number;
  bathroomCount?: number;
  hasLivingRoom?: boolean;
  hasKitchen?: boolean;
  hasDiningArea?: boolean;
  hasBalcony?: boolean;
  sqftArea?: number | null;
  pricePerNight?: number;
  price30Days?: number;
  price40Days?: number;
  durationPrices?: { durationNights: number; price: number }[];
  description?: string;
  floor?: string;
  hasAc?: boolean;
  hasMassageRoom?: boolean;
  totalRooms?: number;
  sortOrder?: number;
}

export type UpdateRoomPayload = Partial<Omit<CreateRoomPayload, "businessId">>;

const roomKeys = {
  list: (businessId: string) => ["rooms", "list", businessId] as const,
};

export const useRoomList = (businessId: string) =>
  useQuery<RoomTypeEntity[]>({
    queryKey: roomKeys.list(businessId),
    queryFn: () => get<RoomTypeEntity[]>("/rooms", { businessId }),
    enabled: !!businessId,
  });

export const useCreateRoom = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRoomPayload) =>
      post<RoomTypeEntity>("/rooms", payload),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: roomKeys.list(businessId) }),
  });
};

export const useCopyRoom = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post<RoomTypeEntity>(`/rooms/${id}/copy`, {}),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: roomKeys.list(businessId) }),
  });
};

export const useUpdateRoom = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoomPayload }) =>
      patch<RoomTypeEntity>(`/rooms/${id}`, data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: roomKeys.list(businessId) }),
  });
};

export const useDeleteRoom = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/rooms/${id}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: roomKeys.list(businessId) }),
  });
};

export const useReorderRooms = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (order: string[]) =>
      patch("/rooms/reorder", { businessId, order }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: roomKeys.list(businessId) }),
  });
};

export const useUploadRoomPhotos = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, files }: { roomId: string; files: File[] }) => {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      return postForm<RoomPhoto[]>(`/rooms/${roomId}/photos`, fd);
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: roomKeys.list(businessId) }),
  });
};

export const useDeleteRoomPhoto = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, photoId }: { roomId: string; photoId: string }) =>
      del(`/rooms/${roomId}/photos/${photoId}`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: roomKeys.list(businessId) }),
  });
};

export const useReorderRoomPhotos = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, order }: { roomId: string; order: string[] }) =>
      patch(`/rooms/${roomId}/photos/reorder`, { order }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: roomKeys.list(businessId) }),
  });
};

export const useSetCoverRoomPhoto = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, photoId }: { roomId: string; photoId: string }) =>
      patch(`/rooms/${roomId}/photos/${photoId}/cover`, {}),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: roomKeys.list(businessId) }),
  });
};

export const useAddRoomPhotoFromUrl = (businessId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, url }: { roomId: string; url: string }) =>
      post<RoomPhoto>(`/rooms/${roomId}/photos/from-url`, { url }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: roomKeys.list(businessId) }),
  });
};
